import React from "react";
import Hero from "../../components/home/Hero";
import ModernServicesSection from "../../components/home/ModernServicesSection";
import CallToActionBanner from "@/components/ui/CallToActionBanner";
import { motion } from "framer-motion";
import Why1 from "../../components/home/Why1";
import Why2 from "../../components/home/Why2";
import PricingSection from "@/components/home/PricingSection";
import Testimonials from "@/components/home/TestimonialsSection";
import { VetComparisonSection } from "../../components/home/VetComparisonSection";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/useIsMobile";
import MobileHomePage from "@/pages/mobile/MobileHomePage";
import AppDownloadSection from "@/components/home/AppDownloadSection";

import ScrollTimeline from "@/components/ui/ScrollTimeline";

const HomePage: React.FC = () => {
  // ⚠️ IMPORTANTE: Todos los hooks deben estar ANTES de cualquier return condicional
  const { isMobile } = useIsMobile();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Si es móvil, renderizar versión optimizada
  if (isMobile) {
    return <MobileHomePage />;
  }

  // Desktop version continúa aquí
  const baseUrl = "https://vettconsult.com";
  const currentLang = i18n.language || "en";

  // SEO data from translations
  const seoTitle = t("seo.home.title");
  const seoDescription = t("seo.home.description");
  const seoKeywords = t("seo.home.keywords");
  const seoOgImage = `${baseUrl}${t("seo.home.ogImage")}`;

  // Canonical URL
  const canonicalUrl = `${baseUrl}${location.pathname}`;

  // Alternate URLs for hreflang
  const alternateUrls = {
    en: `${baseUrl}/en`,
    es: `${baseUrl}/es`,
    fr: `${baseUrl}/fr`,
  };

  // Structured Data - Organization
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "VettConsult",
    url: baseUrl,
    logo: `${baseUrl}/images/logo/LogoVettConsultTransparent.webp`,
    description: seoDescription,
    sameAs: [
      "https://www.facebook.com/vettconsult",
      "https://www.linkedin.com/company/vettconsult",
      "https://twitter.com/vettconsult",
      "https://www.instagram.com/vettconsult",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Service",
      email: "support@vettconsult.com",
      availableLanguage: ["English", "Spanish", "French"],
    },
  };

  // Structured Data - WebSite
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "VettConsult",
    url: baseUrl,
  };

  // Structured Data - Service
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Veterinary Practice Management Software",
    provider: {
      "@type": "Organization",
      name: "VettConsult",
    },
    areaServed: "Worldwide",
    availableLanguage: ["English", "Spanish", "French"],
    description: seoDescription,
    offers: {
      "@type": "Offer",
      availability: "https://schema.org/InStock",
      price: "0",
      priceCurrency: "USD",
      description: "Free trial available",
    },
  };

  return (
    <div className="bg-gradient-to-b from-[#ccfbf1] via-[#d1fae5] to-[#ccfbf1]">
      <ScrollTimeline />
      <Helmet>
        {/* Primary Meta Tags */}
        <html lang={currentLang} />
        <title>{seoTitle}</title>
        <meta name="title" content={seoTitle} />
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />

        {/* Canonical + hreflang are owned by PublicLayout (single source of
            truth). Declaring them here too produced a second, conflicting
            <link rel="canonical"> (child Helmet overrides parent) that pointed
            to a different location than the hreflang alternates — the issue
            Lighthouse flagged. */}

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={seoOgImage} />
        <meta property="og:image:width" content="1563" />
        <meta property="og:image:height" content="1563" />
        <meta property="og:site_name" content="VettConsult" />
        <meta
          property="og:locale"
          content={
            currentLang === "en"
              ? "en_US"
              : currentLang === "es"
                ? "es_ES"
                : "fr_FR"
          }
        />
        {currentLang !== "en" && (
          <meta property="og:locale:alternate" content="en_US" />
        )}
        {currentLang !== "es" && (
          <meta property="og:locale:alternate" content="es_ES" />
        )}
        {currentLang !== "fr" && (
          <meta property="og:locale:alternate" content="fr_FR" />
        )}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoOgImage} />
        <meta name="twitter:creator" content="@vettconsult" />
        <meta name="twitter:site" content="@vettconsult" />

        {/* Additional SEO Meta Tags */}
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
        <meta name="googlebot" content="index, follow" />
        <meta name="bingbot" content="index, follow" />
        <meta name="author" content="VettConsult" />
        <meta name="publisher" content="VettConsult" />
        <meta name="theme-color" content="#00e9be" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <main className="relative">
        {/* Hero Section */}
        <section className="relative h-screen w-full">
          <Hero />
        </section>

        {/* Why Sections */}
        <div id="section-why1">
          <Why1 />
        </div>
        <div id="section-benefits">
          <Why2 />
        </div>

        {/* Other Sections */}
        <div id="section-comparison">
          <VetComparisonSection />
        </div>
        <div id="section-services">
          <ModernServicesSection />
        </div>
        <div id="section-pricing">
          <PricingSection />
        </div>
        <div id="section-testimonials">
          <Testimonials />
        </div>

        {/* Call to Action Banner */}
        <motion.div
          id="section-contact"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, margin: "-100px" }}
          className="relative"
          style={{ margin: 0, padding: 0 }}
        >
          <CallToActionBanner />
        </motion.div>

        {/* App Download Section */}
        <div id="section-app-download">
          <AppDownloadSection />
        </div>
      </main>
    </div>
  );
};

export default HomePage;
